import { CurrencyPipe } from '@angular/common';
import { Component, input, Input, model, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthQuery } from 'app/core/auth/auth.query';
import { BillingService } from 'app/core/my-autoprop/billing/billing.service';
import { EventsService } from 'app/shared/service/events.service';
import { PaymentService } from 'app/shared/service/payment-api.service';
import { FeatureBundle } from 'app/shared/service/subscriptions.model';
import { UiModule } from 'app/shared/ui';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { OverlayRef } from 'app/shared/ui/overlays';
import { guid, loadScript } from 'app/shared/utils';
import { environment } from 'environments/environment';


interface Coupon {
    name: string;
    couponId: string;
    percentOff: number | null;
    amountOff: number | null;
}


@Component({
    selector: 'order-summary',
    imports: [
        CurrencyPipe,
        FormsModule,
        UiModule,
    ],
    styles: [
        `
        .btn-validate {
            height: 35px;
            top: -1px;
            position: relative;
        }

        .disclaimer {
            font-size: smaller;
            color: grey;
        }
        `,
    ],
    template: `
        <ui-modal size="md">
            <modal-header>
                New Subscription
            </modal-header>
            <modal-body class="pl-5 pr-5 pt-4 pb-4">
                <div class="row">
                    <div class="col">
                        <h3>Order Summary</h3>
                    </div>
                </div>
                <div class="row mb-4">
                    <div class="col">
                        {{ featureBundle.name }} bundle
                    </div>
                    <div class="col text-right">
                        <span>{{ originalPrice | currency:'CAD':'symbol-narrow':'1.2-2' }}</span>
                        <span>/{{ billingPeriod }}</span>
                    </div>
                </div>
                <div class="row mb-4">
                    <div class="col">
                        Total
                    </div>
                    <div class="col text-right">
                        {{ originalPrice | currency:'CAD':'symbol-narrow':'1.2-2' }}/{{ billingPeriod }}
                    </div>
                </div>

                @if ((!couponCode() || !couponCodeChecked()) && billingPeriod == 'yearly') {
                    <div class="row">
                        <div class="col-12">
                            <input type="text" class="form-control d-inline w-50" [(ngModel)]="couponCode" placeholder="Coupon code">
                            <button class="btn btn-secondary btn-validate" (click)="checkCouponCode()">
                                <i class="fa fa-refresh"></i>
                                Apply
                            </button>
                        </div>
                    </div>
                }

                @if (couponCode() && couponCodeChecked()) {
                    <div class="row">
                        <div class="col">
                            {{ discountLine }}
                        </div>
                        <div class="col text-right">
                            {{ discount | currency:'CAD':'symbol-narrow':'1.2-2' }}
                        </div>
                    </div>
                }

                <hr>

                <div class="row mb-4">
                    <div class="col">
                        Total before tax
                    </div>
                    <div class="col text-right">
                        {{ totalWithoutTax | currency:'CAD':'symbol-narrow':'1.2-2' }}
                    </div>
                </div>
                <div class="row mb-4">
                    <div class="col">
                        Total including tax
                    </div>
                    <div class="col text-right">
                        {{ totalWithTax | currency:'CAD':'symbol-narrow':'1.2-2' }}
                    </div>
                </div>
                <div class="row mb-2">
                    <div class="col">
                        <form>
                            <div id="payment-form" class="mb-3"></div>
                            @if (stripeShown()) {
                                <div class="disclaimer mb-3">
                                    By clicking “Pay” you agree to Autoprop’s Terms of Use and consent to your personal information
                                    being collected for payment purposes. Payment is processed by Stripe, Inc. on behalf of Autoprop.
                                    Refer to Stripe’s <a href="https://stripe.com/en-ca/privacy" target="_blank">Privacy Policy</a>
                                    and <a href="https://stripe.com/en-ca/legal/consumer" target="_blank">Terms and Conditions</a>.
                                </div>
                                <button class="btn btn-success btn-block" (click)="payAction()" [loading]="paymentInProgress()" [disabled]="paymentInProgress()">
                                    Pay
                                    <span class="loading-text">Processing...</span>
                                </button>
                            }
                        </form>
                        @if (!stripeShown()) {
                            <button class="btn btn-success btn-block" (click)="processPayment()" [loading]="paymentInProgress()" [disabled]="paymentInProgress()">
                                Process payment with {{ savedCard() ? 'saved' : '' }} credit card
                                <span class="loading-text">Processing...</span>
                            </button>
                        }
                        <button class="btn btn-link btn-block" (click)="close()">
                            Cancel
                        </button>
                    </div>
                </div>
            </modal-body>
        </ui-modal>
    `,
})
export class OrderSummaryModalComponent implements OnInit {

    @Input() featureBundle: FeatureBundle;

    @Input() billingPeriod: 'yearly' | 'monthly';

    @Input() customerId: string | null;

    savedCard = input(false);

    @Input() userEmail: string | null;

    stripeLoaded = signal(false);

    stripe: any;

    stripeClientSecret: string;

    stripeElements: any;

    stripeShown = signal(false);

    stripeIntentId: string;

    idempotencyKey = guid();

    totalWithoutTax: number;

    totalWithTax: number;

    originalPrice: number;

    paymentInProgress = signal(false);

    nowTimestamp: number;

    couponCode = model('');

    couponId = '';

    couponCodeChecked = signal(false);

    discount = 0;

    discountLine = '';

    constructor(
        private overlayRef: OverlayRef,
        private es: EventsService,
        private paymentService: PaymentService,
        private modalsService: ModalsService,
        private authQuery: AuthQuery,
        private billingService: BillingService,
    ) {}

    ngOnInit() {
        this.nowTimestamp = new Date().getTime() / 1000;
        this.originalPrice = this.featureBundle.price[this.billingPeriod];
        this.totalWithoutTax = this.featureBundle.price[this.billingPeriod];
        this.totalWithTax = (this.totalWithoutTax / 100) * (100 + this.featureBundle.tax);
        loadScript('https://js.stripe.com/v3/', { onload: () => {
            this.stripeLoaded.set(true);
            this.stripe = (window as any).Stripe(environment.stripeApiKey);
        } });
    }

    showStripe() {
        this.paymentService
            .createCustomer(this.idempotencyKey)
            .subscribe(response => {
                this.customerId = response.payload.customerId;

                this.paymentService.createSetupIntent(this.customerId, this.idempotencyKey)
                    .subscribe({
                        next: response => {
                            this.stripeClientSecret = response.payload.clientSecret;
                            this.stripeIntentId = response.payload.intentId;
                            const appereance = { theme: 'stripe' };
                            this.stripeElements = this.stripe.elements({ appereance, 'clientSecret': this.stripeClientSecret });
                            // https://stripe.com/docs/js/elements_object/create_payment_element#payment_element_create-options
                            const paymentElementOptions = {
                                layout: 'tabs',
                                terms: {
                                    card: 'never'
                                },
                            };

                            const paymentElement = this.stripeElements.create('payment', paymentElementOptions);
                            paymentElement.mount('#payment-form');
                            this.stripeShown.set(true);
                        },
                        error: response => {
                            this.modalsService.alert(response.message, 'Payment error', 'warning');
                        },
                    });
            });
    }

    subscribeBundle() {
        if (this.paymentInProgress()) {
            return;
        }

        this.paymentInProgress.set(true);
        this.paymentService
            .subscribeBundle(
                this.featureBundle.bundleId[this.billingPeriod],
                this.billingPeriod,
                this.couponId
            )
            .subscribe({
                next: response => {
                    this.paymentInProgress.set(false);
                    this.es.billingDetails.next(response.payload.billingDetails);
                    this.authQuery.updateUser({
                        user: {
                            propertiesTableQuotaLeft: response.payload.propertiesTableQuotaLeft,
                            paragonTableQuotaLeft: response.payload.paragonTableQuotaLeft,
                        },
                        resources: response.payload.allowedResources,
                        disclaimers: response.payload.disclaimers,
                    });
                    this.close();
                },
                error: response => {
                    this.paymentInProgress.set(false);
                    this.modalsService.alert(response.message, 'Payment error', 'warning');
                }
            });
    }

    processPayment() {
        if (!this.savedCard()) {
            this.showStripe();
        } else {
            this.subscribeBundle();
        }
    }

    payAction() {
        if (this.paymentInProgress()) {
            return;
        }

        this.paymentInProgress.set(true);

        this.stripe.confirmSetup({
            elements: this.stripeElements,
            redirect: 'if_required'
        }).then((result: any) => {
            if (result.error?.type === 'card_error' || result.error?.type === 'validation_error') {
                this.modalsService.alert(result.error.message, 'Payment error', 'warning');
                this.paymentInProgress.set(false);
                return;
            }

            this.paymentService
                .processPayment(
                    this.stripeIntentId,
                    this.customerId,
                    this.featureBundle.bundleId[this.billingPeriod],
                    this.idempotencyKey,
                    this.billingPeriod,
                    this.couponId
                )
                .subscribe({
                    next: response => {
                        this.paymentInProgress.set(false);
                        this.es.billingDetails.next(response.payload.billingDetails);
                        this.authQuery.updateUser({
                            resources: response.payload.allowedResources,
                            disclaimers: response.payload.disclaimers,
                        });
                        this.close();
                    },
                    error: response => {
                        this.paymentInProgress.set(false);
                        this.modalsService.alert(response.message, 'Payment error', 'warning');
                    }
                });
        });
    }

    close(value = null) {
        this.overlayRef.close(value);
    }

    checkCouponCode() {
        const code = this.couponCode().trim();
        if (code == '') {
            return;
        }

        this.billingService.checkCouponCode(code).subscribe(check => {
            if (check) {
                this.couponCodeChecked.set(true);
                this.couponCode.set(code);
                this.couponId = check.couponId;

                this.recalcSummary(check);
                this.modalsService.alert('Code is valid!', 'Code valid', 'success');
            } else {
                this.couponCodeChecked.set(false);
                this.couponCode.set('');
                this.modalsService.alert('Code not valid', 'Code invalid', 'warning');
            }
        });
    }

    recalcSummary(coupon: Coupon) {
        if (coupon.percentOff) {
            const base = this.featureBundle.price[this.billingPeriod];
            this.totalWithoutTax = base - (base / 100 * coupon.percentOff);
            this.discountLine = `Discount (${coupon.name}, -${coupon.percentOff}%)`;
            this.discount = base / 100 * coupon.percentOff;
        }

        if (coupon.amountOff) {
            this.totalWithoutTax = this.featureBundle.price[this.billingPeriod] - (coupon.amountOff / 100);
            this.discountLine = `Discount (${coupon.name})`;
            this.discount = -1 * (coupon.amountOff / 100);
        }
        this.totalWithTax = (this.totalWithoutTax / 100) * (100 + this.featureBundle.tax);
    }
}
